import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';
import {
  AddUserToAccount,
  GetUserAuthorizedResources,
  GetUsersFromAccount,
  RemoveUserFromAccount,
} from 'src/constants/ApiConstants';
import { GetApiBaseUrl } from 'src/constants/UrlConstants';

import {
  IAddUserToAccountRequest,
  IGetUserAuthorizedForResourcesRequest,
  IGetUserAuthorizedForResourcesResponse,
  IGetUsersForAccountRequest,
  IGetUsersForAccountResponse,
  IRemoveUserFromAccountRequest,
  IRemoveUserFromAccountResponse,
  UserDetails,
} from './models/CaseLifecycleTypes';
import * as KatalMetrics from '@amzn/katal-metrics';
import { emitApiErrorMetrics, MetricsName } from 'src/metrics';
import { logger } from 'src/logger';

const UMS_SERVICE = 'UMS_SERVICE';
export const umsServiceApi = createApi({
  reducerPath: 'umsServiceApi',
  baseQuery: retry(
    fetchBaseQuery({
      baseUrl: GetApiBaseUrl(),
    }),
  ),
  tagTypes: [UMS_SERVICE],
  endpoints: (builder) => ({
    /**
     * GetUserAuthorizedResources will fetch the list of UMS accounts available for the user.
     */
    getUserAuthorizedResources: builder.query<
      IGetUserAuthorizedForResourcesResponse,
      Partial<IGetUserAuthorizedForResourcesRequest>
    >({
      query: (requestBody) => ({
        url:
          requestBody.isPublicPage && requestBody.isRegulatorCentralApp
            ? GetUserAuthorizedResources.getPublicRoute()
            : GetUserAuthorizedResources.getRoute(),
        method: GetUserAuthorizedResources.method,
        body: JSON.stringify(requestBody),
        responseHandler: 'content-type',
      }),
      extraOptions: {
        maxRetries: GetUserAuthorizedResources.numberOfRetries,
      },
      providesTags: [UMS_SERVICE],
      async onQueryStarted(request, { queryFulfilled }) {
        const metric = new KatalMetrics.Metric.HttpRequest(
          MetricsName.BACKEND_REQUEST,
        ).withMonitor();
        try {
          logger.info(
            `Calling getUserAuthorizedResources API with request: ${JSON.stringify(
              request,
            )}`,
          );
          const { data } = await queryFulfilled;
          logger.info(
            `Fetched successful response for getUserAuthorizedResources API for the user: ${data.userId}`,
          );
          metric.setSuccess();
        } catch (error: any) {
          logger.error(
            `Error occured while fetching getUserAuthorizedResources API ${JSON.stringify(
              error,
            )}`,
          );
          emitApiErrorMetrics(
            error,
            GetUserAuthorizedResources.metricsPublisher,
          );
          metric.setFailure();
        }
        GetUserAuthorizedResources.metricsPublisher.publish(metric);
      },
      transformResponse: (response: IGetUserAuthorizedForResourcesResponse) => {
        const resourceAttributes = response?.resourceList.sort((a, b) =>
          a.attributes?.accountName > b.attributes?.accountName ? 1 : -1,
        );
        response.resourceList = resourceAttributes;
        return response;
      },
    }),
    /**
     * GetUsersForAccount will fetch the list of user associated with an account.
     */
    getUsersFromAccount: builder.query<
      IGetUsersForAccountResponse,
      Partial<IGetUsersForAccountRequest>
    >({
      query: (requestBody) => ({
        url: GetUsersFromAccount.getRoute(),
        method: GetUsersFromAccount.method,
        body: JSON.stringify(requestBody),
        responseHandler: 'content-type',
      }),
      extraOptions: {
        maxRetries: GetUsersFromAccount.numberOfRetries,
      },
      providesTags: [UMS_SERVICE],
      async onQueryStarted(request, { queryFulfilled }) {
        const metric = new KatalMetrics.Metric.HttpRequest(
          MetricsName.BACKEND_REQUEST,
        ).withMonitor();
        try {
          logger.info(
            `Calling getUsersFromAccount API with request: ${JSON.stringify(
              request,
            )}`,
          );
          const { data } = await queryFulfilled;

          const dataToBeLogged = data.userDetailsList.map(
            (userDetails: UserDetails) => userDetails.userId,
          );
          logger.info(
            `Response for getUsersFromAccount API is: ${JSON.stringify(
              dataToBeLogged,
            )}`,
          );
          metric.setSuccess();
        } catch (error: any) {
          logger.error(
            `Error occured while fetching getUsersFromAccount API ${JSON.stringify(
              error,
            )}`,
          );
          emitApiErrorMetrics(error, GetUsersFromAccount.metricsPublisher);
          metric.setFailure();
        }
        GetUsersFromAccount.metricsPublisher.publish(metric);
      },
    }),
    addUserToAccount: builder.mutation<any, Partial<IAddUserToAccountRequest>>({
      query: (requestBody) => ({
        url: AddUserToAccount.getRoute(),
        method: AddUserToAccount.method,
        body: JSON.stringify(requestBody),
        responseHandler: 'content-type',
      }),
      invalidatesTags: [UMS_SERVICE],
      extraOptions: {
        maxRetries: 0,
      },
      async onQueryStarted(request, { queryFulfilled }) {
        const metric = new KatalMetrics.Metric.HttpRequest(
          MetricsName.BACKEND_REQUEST,
        ).withMonitor();
        try {
          logger.info(
            `Calling AddUserToAccount API with request: ${JSON.stringify(
              request,
            )}`,
          );
          const { data } = await queryFulfilled;
          logger.info(
            `Response for AddUserToAccount API is: ${JSON.stringify(data)}`,
          );
          metric.setSuccess();
        } catch (error: any) {
          logger.error(
            `Error occured while calling AddUserToAccount API ${JSON.stringify(
              error,
            )}`,
          );
          emitApiErrorMetrics(error, AddUserToAccount.metricsPublisher);
          metric.setFailure();
        }
        AddUserToAccount.metricsPublisher.publish(metric);
      },
    }),
    removeUserFromAccount: builder.mutation<
      IRemoveUserFromAccountResponse,
      Partial<IRemoveUserFromAccountRequest>
    >({
      query: (requestBody) => ({
        url: RemoveUserFromAccount.getRoute(),
        method: RemoveUserFromAccount.method,
        body: JSON.stringify(requestBody),
        responseHandler: 'content-type',
      }),
      invalidatesTags: [UMS_SERVICE],
      extraOptions: {
        maxRetries: 0,
      },
      async onQueryStarted(request, { queryFulfilled }) {
        const metric = new KatalMetrics.Metric.HttpRequest(
          MetricsName.BACKEND_REQUEST,
        ).withMonitor();
        try {
          logger.info(
            `Calling RemoveUserFromAccount API with request: ${JSON.stringify(
              request,
            )}`,
          );
          const { data } = await queryFulfilled;
          logger.info(
            `Response for RemoveUserFromAccount API is: ${JSON.stringify(
              data,
            )}`,
          );
          metric.setSuccess();
        } catch (error: any) {
          logger.error(
            `Error occured while calling RemoveUserFromAccount API ${JSON.stringify(
              error,
            )}`,
          );
          emitApiErrorMetrics(error, RemoveUserFromAccount.metricsPublisher);
          metric.setFailure();
        }
        RemoveUserFromAccount.metricsPublisher.publish(metric);
      },
    }),
  }),
});

/* Automatically generated hooks for each endpoint query */
export const {
  useAddUserToAccountMutation,
  useRemoveUserFromAccountMutation,
  useGetUserAuthorizedResourcesQuery,
  useGetUsersFromAccountQuery,
  endpoints,
  reducerPath,
  middleware,
} = umsServiceApi;
